/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Common app router
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.0.0
 */

/* eslint-disable jsx-a11y/no-access-key */
import { PureComponent } from 'react';

import includes from 'lodash/includes';
import { connect } from 'react-redux';
import { Route, RouteComponentProps, Router, Switch, withRouter } from 'react-router';
import { createStructuredSelector } from 'reselect';

import { EBasketType } from '@modules/app/appConstants';
import appHistory, { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectIsAppLoaded } from '@modules/app/appSelectors';
import { EBreadCrumbType } from '@modules/breadCrumbs/breadCrumbsTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled, makeSelectGuardIsLoaded } from '@modules/role/selectors/guardSelector';
import ChangeUserServiceAccessPage from '@pages/ChangeUserServiceAccessPage/Loadable';
import CorporatePage from '@pages/CorporatePage/CorporatePage';
import PageNotFound from '@pages/ErrorPage/PageNotFound';
import { FastAdvanceInvoice } from '@pages/FastAdvanceInvoice/FastAdvanceInvoice';
import { FastInquiryCreate } from '@pages/FastInquiryCreate/FastInquiryCreate';
import { FastPromisedPayment } from '@pages/FastPromisedPayment/FastPromisedPayment';
import LogoutPage from '@pages/LogoutPage/LogoutPage';
import MoneyTransfer from '@pages/MoneyTransfer/Loadable';
import AccountInfoRoute from '@pages/PageAccountInfo/Loadable';
import {
    makeSelectAuthAuthorities,
    makeSelectAuthIsForbidden,
    makeSelectAuthIsLoading,
    makeSelectIsDashboardAvailable,
    makeSelectIsSingleAccount,
    makeSelectIsSubscriber,
    makeSelectLoggedIn,
    makeSelectorAccountId,
    makeSelectorBisId,
} from '@pages/PageAuth/selectors/authSelectors';
import { IAuthority } from '@pages/PageAuth/types/authTypes';
import PageAutoReportId from '@pages/PageAutoReportId/Loadable';
import { setAutoReportIdBreadCrumbs } from '@pages/PageAutoReportId/utils/setAutoReportIdBreadCrumbs';
import PageBills from '@pages/PageBills/Loadable';
import PageAccountCashbackRoute from '@pages/PageCashback/Loadable';
import { PageCashbackRouteConfiguration } from '@pages/PageCashback/PageCashbackRoute';
import PageCollectiveProduct from '@pages/PageCollectiveProduct/Loadable';
import { makeSelectorWidgetEmptyList } from '@pages/PageDashboard/components/Widgets/widgetSelectors';
import PageExpensesAnalyzer from '@pages/PageExpensesAnalyzer/Loadable';
import { MapExpensesAnalyzerTypeToMeta } from '@pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerMapTypes';
import PageHistoryInquiry from '@pages/PageHistoryInquiry/Loadable';
import PageHistoryOrder from '@pages/PageHistoryOrder/Loadable';
import PageInquiryInfo from '@pages/PageInquiryInfo/Loadable';
import PageLabelsImport from '@pages/PageLabelsImport/Loadable';
import PageOrderId from '@pages/PageOrderId/Loadable';
import PagePayments from '@pages/PagePayments/Loadable';
import PageProducts from '@pages/PageProducts/Loadable';
import PageReportsConstructor from '@pages/PageReportsConstructor/Loadable';
import PageReportsSubscriber from '@pages/PageReportsSubscriber/Loadable';
import PageSettings from '@pages/PageSettings/Loadable';
import PageSubscriberIdRoute from '@pages/PageSubscriberId/Loadable';
import { setPageSubscriberIdBreadcrumbs } from '@pages/PageSubscriberId/utils/setPageSubscriberIdBreadcrumbs';
import PageSubscriberMobile from '@pages/PageSubscriberMobile/Loadable';
import WizardAdditionalAccounts from '@pages/WizardAdditionalAccounts/components/WizardAdditionalAccounts';
import WizardAdditionalReports from '@pages/WizardAdditionalReports/components/WizardAdditionalReports';
import WizardBillsDoc from '@pages/WizardBillsDoc/Loadable';
import PageWizardCallsDetail from '@pages/WizardCallsDetail/Loadable';
import WizardChangeLock from '@pages/WizardChangeLock/Loadable';
import WizardChangeRole from '@pages/WizardChangeRole/components/WizardChangeRole';
import WizardExpensesDetail from '@pages/WizardExpensesDetail/components/WizardExpensesDetail';
import WizardFastBillsDoc from '@pages/WizardFastBillsDoc/components/WizardFastBillsDoc';
import WizardImportPersonalData from '@pages/WizardImportPersonalData/Loadable';
import WizardMultiSearch from '@pages/WizardMultiSearch/Loadable';
import WizardProductOfferings from '@pages/WizardProductOfferings/Loadable';
import WizardOrderReport from '@pages/WizardReportsConstructor/WizardOrderReport/components/WizardOrderReport';
import WizardOrderTemplate from '@pages/WizardReportsConstructor/WizardOrderTemplate/Loadable';
import { IRootReducer } from '@reducers/rootReducer';
import { WizardAdditionalReportsRouteConfiguration } from '@routes/constants/wizardAdditionaReportsConfiguration';
import { WizardChangeRoleRouteConfiguration } from '@routes/constants/wizardChangeRoleConfiguration';

import WizardCollationReport from '../pages/WizardCollationReport/components/WizardCollationReport';
import WizardImportSubscribersLimits from '../pages/WizardImportSubscribersLimits/components/WizardImportSubscribersLimits';
import { IAppRouterConfiguration, IWizardFastAcions, IWizardRouterConfiguration } from './appRouterTypes';
import { AnalyzerRouteConfiguration, IAnalyzerRouteConfiguration } from './constants/analyzerConfiguration';
import { BillsRouteConfiguration, IBillsRouteConfiguration } from './constants/billsConfigurator';
import { CallsDetailRouteConfiguration } from './constants/callsDetailRouteConfiguration';
import { openExternalRoute, openRoute } from './constants/externalRoutes';
import {
    IMoneyTransferRouteConfiguration,
    MoneyTransferRouteConfiguration,
} from './constants/moneyTransferConfiguration';
import { PaymentsRouteConfiguration } from './constants/paymentsRouteConfiguration';
import { ProductOfferingsRouteConfiguration } from './constants/productOfferingsConfiguration';
import { IProductsRouteConfiguration, ProductsRouteConfiguration } from './constants/productsConfiguration';
import { ReportsConstructorRouteConfiguration } from './constants/reportsConstructorConfiguration';
import { CLIENT_ID, RoutePath } from './constants/routesConstants';
import { WizardAdditionalAccountsRouteConfiguration } from './constants/wizardAdditionalAccountsConfiguration';
import { WizardBillsDocRouteConfiguration } from './constants/wizardBillsDocConfigurations';
import { WizardChangeLockRouteConfiguration } from './constants/wizardChangeLockConfiguration';
import { WizardCollationReportRouteConfiguration } from './constants/wizardCollationReportConfiguration';
import { WizardExpensesDetailRouteConfiguration } from './constants/wizardExpensesDetailConfiguration';
import { WizardFastActionsRouteConfiguration } from './constants/wizardFasActionsConfiguration';
import { WizardImportPersonalDataRouteConfiguration } from './constants/wizardImportPersonalDataConfiguration';
import { WizardOrderReportConfiguration } from './constants/wizardOrderReportConfiguration';
import { WizardOrderTemplateCreateConfiguration } from './constants/wizardOrderTemplateCreateConfiguration';
import { WizardOrderTemplateEditConfiguration } from './constants/wizardOrderTemplateEditConfiguration';
import AccountsRoute from './nested/AccountsRoute';
import CampaignsRoute from './nested/CampaignsRoute';
import DashboardRoute from './nested/DashboardRoute';
import ForbiddenRoute from './nested/ForbiddenRoute';
import HomeRoute from './nested/HomeRoute';
import LoginRoute from './nested/LoginRoute';
import LoginServiceAccessRoute from './nested/LoginServiceAccessRoute';
import NewsRoute from './nested/NewsRoute';
import UnauthorizedRoute from './nested/UnauthorizedRoute';
import PrivateRoute from './PrivateRoute';

/**
 * @description Define all routes in app.
 * @interface IRoutePath
 * @see RoutePath
 * @see PrivateRoute
 * @see LoginNested
 */
export class AppRouter extends PureComponent<RouteComponentProps & IAppRouterConnectedProps> {
    pageRedirectLogic = () => {
        const {
            accountId,
            bisId,
            isAccountInfoGeneralEnabled,
            isAppLoaded,
            isGuardLoaded,
            isLoading,
            isSingleAccount,
            isSubscriber,
            location,
            loggedIn,
        } = this.props;

        if (location.pathname === RoutePath.unauthorized) {
            return true;
        }
        if (!isAppLoaded || isLoading) {
            return null;
        }

        if (loggedIn && isGuardLoaded) {
            const query = new URLSearchParams(document.location.search);
            const redirectUrl = query.get('r');
            if (redirectUrl) {
                openRoute(redirectUrl);
                return null;
            }

            if (!isSubscriber && isSingleAccount && location.pathname === RoutePath.account) {
                if (isAccountInfoGeneralEnabled) {
                    appHistoryPush(RoutePath.accountInfoGeneral.replace(CLIENT_ID, accountId.toString()));
                } else {
                    openExternalRoute(RoutePath.accountInfoExternal.replace(CLIENT_ID, accountId.toString()));
                }
                return null;
            }

            if (isSubscriber) {
                if (
                    location.pathname === RoutePath.account ||
                    includes(location.pathname, RoutePath.subscriberMobile)
                ) {
                    openRoute(RoutePath.subscriberId.replace(CLIENT_ID, bisId.toString()));
                    return null;
                }
            }
        }
    };

    render() {
        if (this.pageRedirectLogic() === null) {
            return null;
        }
        return (
            <Router history={appHistory}>
                {this.props.children}
                <Switch>
                    {/* Home Page */}
                    <Route component={HomeRoute} exact={true} path="/" />

                    {/* Dashboard Page */}
                    <Route component={DashboardRoute} path={RoutePath.dashboard} />

                    {/* ExpensesAnalyzer Account page */}
                    {AnalyzerRouteConfiguration.map((configuration: IAnalyzerRouteConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageExpensesAnalyzer}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageExpensesAnalyzer}
                            componentProps={{
                                ...MapExpensesAnalyzerTypeToMeta[configuration.type],
                                type: configuration.type,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Login, remember */}
                    <Route component={LoginRoute} path={RoutePath.login} />

                    {/* Login service access */}
                    <Route component={LoginServiceAccessRoute} path={RoutePath.serviceAccessLogin} />

                    {/* Logout */}
                    <Route component={LogoutPage} path={RoutePath.logout} />
                    {/* Unauthorized */}
                    <Route component={UnauthorizedRoute} path={RoutePath.unauthorized} />
                    {/* Change User (service access only) */}
                    <Route component={ChangeUserServiceAccessPage} path={RoutePath.serviceAccessChangeUser} />

                    {/* Accounts Pages */}
                    <Route component={AccountsRoute} path={RoutePath.account} />
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageAccountInfo}
                        breadCrumbsDisabled={true}
                        component={AccountInfoRoute}
                        path={RoutePath.accountInfo}
                        wrapper={CorporatePage}
                    />

                    {/* Accounts Cashback */}
                    {PageCashbackRouteConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            accessKey={AccessComponentKeyEnum.PageAccountCashback}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageAccountCashbackRoute}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Products */}
                    {ProductsRouteConfiguration.map((configuration: IProductsRouteConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageProducts}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageProducts}
                            componentProps={{
                                pageFrom: configuration.pageFrom,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Wizard Product Offerings */}
                    {ProductOfferingsRouteConfiguration.map((configuration: IProductsRouteConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.WizardProductOfferings}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardProductOfferings}
                            componentProps={{
                                basketType: configuration.basketType,
                                pageFrom: configuration.pageFrom,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {WizardExpensesDetailRouteConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardExpensesDetail}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardExpensesDetail}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* PageBills */}
                    {BillsRouteConfiguration.map((configuration: IBillsRouteConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageBills}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageBills}
                            componentProps={{
                                type: configuration.type,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* PageSubscriberId */}
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageSubscriberId}
                        breadCrumbs={setPageSubscriberIdBreadcrumbs(
                            this.props.isReportsConstructorAvailable,
                            this.props.location,
                        )}
                        component={PageSubscriberIdRoute}
                        path={RoutePath.subscriberId}
                        wrapper={CorporatePage}
                    />

                    {/* Page Subscriber Mobile*/}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageSubscriberMobile}
                        component={PageSubscriberMobile}
                        path={RoutePath.subscriberMobile}
                        wrapper={CorporatePage}
                    />

                    {/* PageHistoryOrder */}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageHistoryOrder}
                        component={PageHistoryOrder}
                        path={RoutePath.orders}
                        wrapper={CorporatePage}
                    />
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageOrderId}
                        breadCrumbs={[EBreadCrumbType.orders, EBreadCrumbType.orderId]}
                        component={PageOrderId}
                        path={RoutePath.orderId}
                        wrapper={CorporatePage}
                    />

                    {/* PageHistoryInquiry */}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageHistoryInquiry}
                        component={PageHistoryInquiry}
                        path={RoutePath.inquiries}
                        wrapper={CorporatePage}
                    />

                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageInquiryInfo}
                        breadCrumbs={[EBreadCrumbType.inquiries, EBreadCrumbType.inquiryInfo]}
                        component={PageInquiryInfo}
                        path={RoutePath.inquiryInfo}
                        wrapper={CorporatePage}
                    />

                    {/* Money Transfer */}
                    {MoneyTransferRouteConfiguration.map((configuration: IMoneyTransferRouteConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageMoneyTransfer}
                            breadCrumbs={configuration.breadCrumbs}
                            component={MoneyTransfer}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Page Labels Import */}
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageLabelsImport}
                        breadCrumbs={[EBreadCrumbType.settingsLabels, EBreadCrumbType.labelsImport]}
                        component={PageLabelsImport}
                        componentProps={{
                            basketType: EBasketType.MOBILE,
                        }}
                        key={RoutePath.labelsImport}
                        path={RoutePath.labelsImport}
                        wrapper={CorporatePage}
                    />

                    {/* Page Settings*/}
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageSettings}
                        component={PageSettings}
                        path={RoutePath.settings}
                        wrapper={CorporatePage}
                    />

                    {/* Page Wizard Calls Detail */}
                    {CallsDetailRouteConfiguration.map((configuration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardCallsDetail}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageWizardCallsDetail}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Page Campaign */}
                    <Route component={CampaignsRoute} path={RoutePath.campaigns} />

                    {/* Page ReportsConstructor */}
                    {ReportsConstructorRouteConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageReportsConstructor}
                            breadCrumbs={configuration.breadCrumbs}
                            component={PageReportsConstructor}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Page Collective Product*/}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageCollectiveProduct}
                        component={PageCollectiveProduct}
                        path={RoutePath.collectiveProduct}
                    />

                    {/* Page Reports Subscriber*/}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.PageReportsSubscriber}
                        breadCrumbs={[
                            EBreadCrumbType.reportsConstructor,
                            EBreadCrumbType.reportsConstructorSubscribers,
                        ]}
                        component={PageReportsSubscriber}
                        path={RoutePath.reportsConstructorSubscriber}
                        wrapper={CorporatePage}
                    />

                    {/* News pages */}
                    <Route component={NewsRoute} path={RoutePath.news} />

                    {/* Additional accounts */}
                    {WizardAdditionalAccountsRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardAdditionalAccounts}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardAdditionalAccounts}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Additional reports */}
                    {WizardAdditionalReportsRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardAdditionalReports}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardAdditionalReports}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Collation report */}
                    {WizardCollationReportRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardCollationReport}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardCollationReport}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {
                        /* Wizard Fast Actions Doc  */
                        WizardFastActionsRouteConfiguration.map((configuration: IWizardFastAcions) => (
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.WizardFastBillsDocs}
                                component={WizardFastBillsDoc}
                                componentProps={{
                                    basketType: configuration.basketType,
                                    operation: configuration.operation,
                                }}
                                key={configuration.path}
                                path={configuration.path}
                                wrapper={CorporatePage}
                            />
                        ))
                    }

                    {
                        /* Wizard Bills Doc  */
                        WizardBillsDocRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PageWizardBillsDocs}
                                breadCrumbs={configuration.breadCrumbs}
                                component={WizardBillsDoc}
                                componentProps={{
                                    basketType: configuration.basketType,
                                }}
                                key={configuration.path}
                                path={configuration.path}
                                wrapper={CorporatePage}
                            />
                        ))
                    }

                    {/* Change lock wizard */}
                    {WizardChangeLockRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardChangeLock}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardChangeLock}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Import Personal Data */}
                    {WizardImportPersonalDataRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardImportPersonalData}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardImportPersonalData}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Wizard Multi Search */}
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageWizardMultiSearch}
                        breadCrumbs={[EBreadCrumbType.subscriberMobile, EBreadCrumbType.wizardMultiSearch]}
                        component={WizardMultiSearch}
                        path={RoutePath.wizardMultiSearch}
                        wrapper={CorporatePage}
                    />

                    {/* Wizard Import Subscribers Limits */}
                    <PrivateRoute
                        accessKey={AccessComponentKeyEnum.PageWizardImportSubscribersLimits}
                        breadCrumbs={[EBreadCrumbType.subscriberMobile, EBreadCrumbType.wizardImportSubscribersLimits]}
                        component={WizardImportSubscribersLimits}
                        path={RoutePath.wizardImportSubscribersLimits}
                        wrapper={CorporatePage}
                    />

                    {/* Wizard Order Template */}

                    {WizardOrderTemplateCreateConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageReportsConstructor}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardOrderTemplate}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {WizardOrderTemplateEditConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageReportsConstructor}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardOrderTemplate}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Wizard Order Report */}

                    {WizardOrderReportConfiguration.map((configuration: IAppRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageReportsConstructor}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardOrderReport}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {/* Change role */}
                    {WizardChangeRoleRouteConfiguration.map((configuration: IWizardRouterConfiguration) => (
                        <PrivateRoute
                            exact
                            accessKey={AccessComponentKeyEnum.PageWizardChangeRole}
                            breadCrumbs={configuration.breadCrumbs}
                            component={WizardChangeRole}
                            componentProps={{
                                basketType: configuration.basketType,
                            }}
                            key={configuration.path}
                            path={configuration.path}
                            wrapper={CorporatePage}
                        />
                    ))}

                    {
                        /* Payments */
                        PaymentsRouteConfiguration.map((configuration: IAppRouterConfiguration) => (
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PagePayments}
                                breadCrumbs={configuration.breadCrumbs}
                                component={PagePayments}
                                key={configuration.path}
                                path={configuration.path}
                                wrapper={CorporatePage}
                            />
                        ))
                    }

                    {
                        /* AutoReportId */
                        <PrivateRoute
                            accessKey={AccessComponentKeyEnum.PageAutoReportId}
                            breadCrumbs={setAutoReportIdBreadCrumbs(this.props.isReportsConstructorAvailable)}
                            component={PageAutoReportId}
                            path={RoutePath.autoReportId}
                            wrapper={CorporatePage}
                        />
                    }

                    {/* Fast actions pages*/}
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.FastInquiryCreate}
                        component={FastInquiryCreate}
                        path={RoutePath.fastInquiryCreate}
                        wrapper={CorporatePage}
                    />
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.FastAdvanceInvoice}
                        component={FastAdvanceInvoice}
                        path={RoutePath.fastAdvanceInvoice}
                        wrapper={CorporatePage}
                    />
                    <PrivateRoute
                        exact
                        accessKey={AccessComponentKeyEnum.FastPromisedPayment}
                        component={FastPromisedPayment}
                        path={RoutePath.fastPromisedPayment}
                        wrapper={CorporatePage}
                    />

                    {/* Page forbidden component */}
                    <Route component={ForbiddenRoute} path={RoutePath.forbidden} />

                    <PrivateRoute accessKey={AccessComponentKeyEnum.PageNotFound} component={PageNotFound} path="**" />
                </Switch>
            </Router>
        );
    }
}

export interface IAppRouterConnectedProps {
    accountId: number;
    authorities: IAuthority[];
    bisId: number;
    emptyWidgets: boolean;
    isAccountInfoGeneralEnabled: boolean;
    isAppLoaded: boolean;
    isForbidden: boolean;
    isGuardLoaded: boolean;
    isLoading: boolean;
    isPageDashboardActive: boolean;
    isSingleAccount: boolean;
    isSubscriber: boolean;
    isReportsConstructorAvailable: boolean;
    loggedIn: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IAppRouterConnectedProps>({
    accountId: makeSelectorAccountId(),
    authorities: makeSelectAuthAuthorities(),
    bisId: makeSelectorBisId(),
    emptyWidgets: makeSelectorWidgetEmptyList(),
    isAccountInfoGeneralEnabled: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageAccountInfoGeneral),
    isAppLoaded: makeSelectIsAppLoaded(),
    isForbidden: makeSelectAuthIsForbidden(),
    isGuardLoaded: makeSelectGuardIsLoaded(),
    isLoading: makeSelectAuthIsLoading(),
    isPageDashboardActive: makeSelectIsDashboardAvailable(),
    isSingleAccount: makeSelectIsSingleAccount(),
    isSubscriber: makeSelectIsSubscriber(),
    isReportsConstructorAvailable: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.reportsConstructor),
    loggedIn: makeSelectLoggedIn(),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default withRouter(connect(mapStateToProps)(AppRouter) as any) as any;
